// frontend/src/widgets/StandardReportsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-30
// Updated:      2026-09-21
// Description:  Widget to display and preview standard reports with date range, field selection,
//               field ordering, persisted report layouts, ANPR runtime parameters,
//               and ANPR time-slot report execution controls.
//               2026-05-12: Added report-run diagnostics and guarded auto-empty-column stripping
//               so valid backend rows cannot render as a zero-column/blank preview.
//               2026-05-18: Added ANPR Duration of Stay - Single Camera controls and parameters.
//               2026-05-18: Added ANPR Duration of Stay - Single Camera Summary report support.
//               2026-05-18: Summary now exposes a single entered-not-exited metric and camera selection by name.
//               2026-05-19: Duration reports now use selected camera entry direction for orientation-aware pairing.
//               2026-07-16: Single-camera Duration of Stay camera options now load from camera_job_bindings, not pairings.
//               2026-05-19: Added ANPR Journey Time - Corridor Detail controls and parameters.
//               2026-09-18: Restored ANPR Sightings – Detail - Time Slots - Pairings
//               configuration support: named time slots, ANPR job selection, V3 source
//               mapping, and runtime movement timing thresholds hydrated from the
//               template settings. The pairing report does not use configured
//               camera-pairing routes as an observed-movement whitelist.
//               2026-09-21: Added friendly selectors for V3 per-sighting and
//               per-movement motion context. Field discovery remains backend-driven,
//               so corridors are not limited to any fixed number of cameras.
//               2026-09-21: Restored independent Sighting Date and Sighting Time
//               selectors derived server-side from Frame Time (frame_ts_utc).
//               2026-09-21: Pairings logical-journey controls now default to 1-300 seconds;
//               Lunch defaults off for the Pairings report. Save Layout now persists the
//               complete report configuration (fields/order, dates, parameters, time slots
//               and single-time windows) and Preview no longer overwrites the saved layout.
//
// Mobile Responsiveness (2026-09-09):
//               - Preserves all report discovery, grouping, configuration, field ordering,
//                 saved layouts, ANPR parameters, validation, preview and export behaviour.
//               - Main report browser uses natural mobile height and grows the first
//                 width-matching fixed-height host using the proven two-pass Vision pattern.
//               - Date/refresh controls stack on mobile and use touch-sized controls.
//               - Report groups remain collapsible; report cards remain the same render tree
//                 and collapse to one column on mobile.
//               - Configuration modal becomes a near-fullscreen mobile sheet with stacked
//                 date/time controls, parameter sections, field panes and footer actions.
//               - Removes the inline two-column field-pane override that defeated the
//                 existing mobile grid class.
//               - Time-slot Start/End controls stack on narrow phones.
//               - No duplicate mobile render tree is mounted.
//               2026-09-16: Fixed configuration-modal mobile clipping by separating
//               fixed header/footer chrome from the single internal scrolling body.
//               Mobile field panes now use the modal body scroll instead of nested
//               320px scrollers; desktop field-pane scrolling remains unchanged.
//
// Operational Notes:
//               - This is a presentation-only mobile pass. API routes, payloads, report
//                 semantics, persisted layout keys and report execution logic are unchanged.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

import axiosInstance from "../api/axiosInstance";
import ReportDisplayModal from "../components/ReportDisplayModal";
import ModalWrapper from "../components/ModalWrapper";
import { useAlerts } from "../context/AlertContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

const LAYOUT_STORAGE_KEY = "vision_anpr_report_layouts_v1";
const JOB_OPTIONS_LIMIT = 100;

const REPORT_NAME_DISPLAY_ALIASES = {
  "ANPR Plate Sessions": "ANPR Plate Activity Summary",
  "ANPR Plate Sessions - Time Slots": "ANPR Plate Activity Summary - Time Slots",
};

const TIME_SLOT_REPORT_NAMES = new Set([
  "ANPR Plate Sessions - Time Slots",
  "ANPR Sightings – Detail - Time Slots",
  "ANPR Sightings – Detail - Time Slots - Pairings",
  "ANPR Sightings – Detail – Time Slots – Pairings",
  "ANPR Weaving – Detail - Time Slots",
  "ANPR Weaving – Movement Detail - Time Slots",
]);

const ANPR_SIGHTINGS_PAIRINGS_REPORT_NAMES = new Set([
  "ANPR Sightings – Detail - Time Slots - Pairings",
  "ANPR Sightings – Detail – Time Slots – Pairings",
]);

const DEFAULT_REPORT_TIME_SLOTS = [
  { key: "am_peak", label: "AM Peak", start_time: "07:00", end_time: "10:00", enabled: true },
  { key: "lunch", label: "Lunch", start_time: "12:00", end_time: "14:00", enabled: true },
  { key: "pm_peak", label: "PM Peak", start_time: "16:00", end_time: "19:00", enabled: true },
];

const ANPR_DURATION_STAY_SINGLE_CAMERA_REPORT_NAMES = new Set([
  "ANPR Duration of Stay – Single Camera",
  "ANPR Duration of Stay - Single Camera",
  "ANPR Duration of Stay – Single Camera - Summary",
  "ANPR Duration of Stay - Single Camera - Summary",
]);

const ANPR_DURATION_STAY_SINGLE_CAMERA_SUMMARY_REPORT_NAMES = new Set([
  "ANPR Duration of Stay – Single Camera - Summary",
  "ANPR Duration of Stay - Single Camera - Summary",
]);

const ANPR_JOURNEY_TIME_CORRIDOR_REPORT_NAMES = new Set([
  "ANPR Journey Time – Corridor Detail",
  "ANPR Journey Time - Corridor Detail",
]);

const ANPR_PARAMETER_REPORTS = new Set([
  "ANPR Duration of Stay – Single Camera",
  "ANPR Duration of Stay - Single Camera",
  "ANPR Duration of Stay – Single Camera - Summary",
  "ANPR Duration of Stay - Single Camera - Summary",
  "ANPR Journey Time – Corridor Detail",
  "ANPR Journey Time - Corridor Detail",
  "ANPR Weaving – Detail",
  "ANPR Weaving – Movement Detail",
  "ANPR Movement – Scenario Detail",
  "ANPR Weaving – Scenario Summary",
  "ANPR Average Speed – Detail",
  "ANPR Average Speed – Summary",
  "ANPR Plate Sessions - Time Slots",
  "ANPR Sightings – Detail - Time Slots",
  "ANPR Sightings – Detail - Time Slots - Pairings",
  "ANPR Sightings – Detail – Time Slots – Pairings",
  "ANPR Weaving – Detail - Time Slots",
  "ANPR Weaving – Movement Detail - Time Slots",
]);

const SCENARIO_OPTIONS = [
  { code: "weaving", label: "Weaving Movement" },
  { code: "route_traversal", label: "Through Movement" },
  { code: "exit_no_rejoin", label: "Exit / No Rejoin" },
  { code: "end_no_start", label: "End Camera / No Prior Start" },
  { code: "below_min_travel_time", label: "Below Minimum Travel Time" },
  { code: "late_reappearance", label: "Late Reappearance" },

  // Legacy value kept temporarily so old saved UI state does not silently break.
  { code: "invalid_too_fast", label: "Below Minimum Travel Time (Legacy)" },
];

const JOURNEY_SCENARIO_OPTIONS = [
  { code: "completed_start_to_end", label: "Completed: Start → End" },
  { code: "completed_end_to_start", label: "Completed: End → Start" },
  { code: "start_no_end", label: "Start Seen / No End Capture" },
  { code: "end_no_start", label: "End Seen / No Prior Start" },
];

const formatPairingDistance = (pairing) => {
  const raw = pairing?.distance_meters;
  if (raw === undefined || raw === null || raw === "") return "Distance not set";
  const metres = Number(raw);
  if (!Number.isFinite(metres) || metres <= 0) return "Distance not set";
  if (metres >= 1000) return `${(metres / 1000).toFixed(2)} km`;
  return `${metres.toFixed(0)} m`;
};

const EMPTY_REPORT_PARAMETERS = {
  job_id: "",
  route_code: "",
  scenario_code: "",
  pairing_id: "",
  camera_device_id: "",
  camera_name: "",
  start_camera_device_id: "",
  end_camera_device_id: "",
  start_camera_name: "",
  end_camera_name: "",
  movement_direction: "",
  entry_motion: "towards",
  movement_pattern: "",
  min_dwell_minutes: "2",
  max_dwell_minutes: "360",
  min_journey_minutes: "0.5",
  max_journey_minutes: "60",
  expected_leg_seconds: "1",
  journey_timeout_seconds: "300",
};

const REPORT_VIEW_SOURCES = {
  "Sales Pipeline – Current Snapshot": "v_opportunity_pipeline_summary",
  "Sales Outcomes – Month to Date": "v_opportunity_mtd_outcomes",
  "Sales Outcomes – Year to Date": "v_opportunity_ytd_outcomes",
  "Opportunities – Ageing Analysis": "v_opportunity_ageing",

  "Jobs – Current Status Overview": "jobs_with_latest_assignment",
  "Job Assignments – Detailed": "job_assignments_expanded_v1",
  "Job Classification Summary": "v_job_taxonomy",

  "ANPR Plate Sessions": "v_anpr_job_unique_plate_summary",
  "ANPR Plate Sessions - Time Slots": "v_anpr_job_unique_plate_summary",
  "ANPR Plate Activity Summary": "v_anpr_job_unique_plate_summary",
  "ANPR Plate Activity Summary - Time Slots": "v_anpr_job_unique_plate_summary",

  "ANPR Sightings – Detail": "anpr_sightings_enriched_v1",
  "ANPR Sightings – Detail - Time Slots": "anpr_sightings_enriched_v1",
  "ANPR Sightings – Detail - Time Slots - Pairings": "anpr_sightings_enriched_v3",
  "ANPR Sightings – Detail – Time Slots – Pairings": "anpr_sightings_enriched_v3",
  "ANPR Duration of Stay – Single Camera": "v_anpr_duration_of_stay_single_camera",
  "ANPR Duration of Stay - Single Camera": "v_anpr_duration_of_stay_single_camera",
  "ANPR Duration of Stay – Single Camera - Summary": "v_anpr_duration_of_stay_single_camera_summary",
  "ANPR Duration of Stay - Single Camera - Summary": "v_anpr_duration_of_stay_single_camera_summary",
  "ANPR Journey Time – Corridor Detail": "v_anpr_journey_time_corridor_detail",
  "ANPR Journey Time - Corridor Detail": "v_anpr_journey_time_corridor_detail",
  "ANPR Unique Vehicles – Job Summary": "v_anpr_job_unique_plate_summary",
  "ANPR Vehicles – Make Model Detail": "anpr_sightings_enriched_v1",
  "ANPR Compliance – Tax and MOT": "anpr_sightings_enriched_v1",
  "ANPR Vehicle Classification": "anpr_sightings_enriched_v1",

  "ANPR Weaving – Detail": "v_anpr_weaving_report",
  "ANPR Weaving – Detail - Time Slots": "v_anpr_weaving_report",
  "ANPR Weaving – Movement Detail": "v_anpr_weaving_movement_detail",
  "ANPR Movement – Scenario Detail": "v_anpr_weaving_movement_detail",
  "ANPR Weaving – Movement Detail - Time Slots": "v_anpr_weaving_movement_detail",
  "ANPR Weaving – Scenario Summary": "v_anpr_weaving_scenario_summary",
  "ANPR Average Speed – Detail": "v_anpr_weaving_report",
  "ANPR Average Speed – Summary": "v_anpr_weaving_scenario_summary",

  "Customer Conversations – Overview": "conversation_overview_v1",
  "Portal Access – Jobs": "portal_job_access_v1",
};

const REPORT_VIRTUAL_FIELDS_FALLBACK = {
  "ANPR Plate Sessions": ["best_snapshot_url", "plate_image_url"],
  "ANPR Plate Sessions - Time Slots": ["best_snapshot_url", "plate_image_url"],
  "ANPR Plate Activity Summary": ["best_snapshot_url", "plate_image_url"],
  "ANPR Plate Activity Summary - Time Slots": ["best_snapshot_url", "plate_image_url"],
  "ANPR Sightings – Detail": ["plate_image_url", "best_snapshot_url"],
  "ANPR Sightings – Detail - Time Slots": ["plate_image_url", "best_snapshot_url"],
  "ANPR Sightings – Detail - Time Slots - Pairings": ["plate_image_url", "best_snapshot_url"],
  "ANPR Sightings – Detail – Time Slots – Pairings": ["plate_image_url", "best_snapshot_url"],
  "ANPR Unique Vehicles – Job Summary": ["plate_image_url", "best_snapshot_url"],
  "ANPR Vehicles – Make Model Detail": ["plate_image_url", "best_snapshot_url"],
  "ANPR Compliance – Tax and MOT": ["plate_image_url", "best_snapshot_url"],
  "ANPR Vehicle Classification": ["plate_image_url", "best_snapshot_url"],
  "ANPR Duration of Stay – Single Camera": [
    "first_snapshot_url",
    "last_snapshot_url",
    "first_plate_secondary_url",
    "last_plate_secondary_url",
    "first_overview_url",
    "last_overview_url",
  ],
  "ANPR Duration of Stay - Single Camera": [
    "first_snapshot_url",
    "last_snapshot_url",
    "first_plate_secondary_url",
    "last_plate_secondary_url",
    "first_overview_url",
    "last_overview_url",
  ],
  "ANPR Journey Time – Corridor Detail": [
    "start_snapshot_url",
    "end_snapshot_url",
    "start_plate_secondary_url",
    "end_plate_secondary_url",
    "start_overview_url",
    "end_overview_url",
  ],
  "ANPR Journey Time - Corridor Detail": [
    "start_snapshot_url",
    "end_snapshot_url",
    "start_plate_secondary_url",
    "end_plate_secondary_url",
    "start_overview_url",
    "end_overview_url",
  ],
};

const FRIENDLY_FIELD_NAMES = {
  job_id: "Job ID",
  client_name: "Client",
  status: "Status",
  created_at: "Created At",
  id: "ID",
  username: "Username",
  email: "Email",
  first_name: "First Name",
  last_name: "Last Name",
  role: "Role",
  last_login_at: "Last Login",
  last_active_at: "Last Active",
  timestamp: "Timestamp",
  run_id: "Run ID",
  frame_ts_utc: "Frame Time",
  sighting_date: "Sighting Date",
  sighting_time: "Sighting Time",
  plate_normalized: "Plate",
  plate: "Plate",
  display_plate: "Display Plate",
  yolo_conf: "Confidence",
  camera_name: "Camera",
  motion: "Motion",
  previous_sighting_motion: "Previous Sighting Motion",
  next_sighting_motion: "Next Sighting Motion",
  previous_distinct_camera_motion: "Previous Camera Motion",
  next_distinct_camera_motion: "Next Camera Motion",
  movement_from_motion: "Movement From Motion",
  movement_to_motion: "Movement To Motion",
  sighting_vehicle_make: "Camera Make",
  sighting_vehicle_model: "Camera Model",
  sighting_vehicle_color: "Camera Colour",
  sighting_vehicle_category: "Camera Category",
  vehicle_make: "Resolved Make",
  vehicle_model: "Resolved Model",
  vehicle_colour: "Resolved Colour",
  vehicle_fuel_type: "Fuel Type",
  vehicle_type_hint: "Type Hint",
  dvla_make: "DVLA Make",
  dvla_colour: "DVLA Colour",
  dvla_fuel_type: "DVLA Fuel",
  dvla_year_of_manufacture: "Year",
  dvla_tax_status: "Tax Status",
  dvla_tax_due_date: "Tax Due",
  dvla_mot_status: "MOT Status",
  dvla_mot_expiry_date: "MOT Expiry",
  dvla_engine_capacity_cc: "Engine CC",
  dvla_co2_emissions_gpkm: "CO2 g/km",
  dvla_revenue_weight_kg: "Revenue Weight KG",
  dvla_wheelplan: "Wheelplan",
  dvla_type_approval: "Type Approval",
  dvla_fetched_at: "DVLA Cached At",
  dvla_cached_at: "DVLA Applied At",
  sighting_count: "Sightings",
  first_seen_at: "First Seen",
  last_seen_at: "Last Seen",
  first_seen_s: "First Seen",
  last_seen_s: "Last Seen",
  dwell_s: "Dwell (s)",
  hit_count: "Hits",
  max_yolo_conf: "Max Confidence",
  avg_yolo_conf: "Avg Confidence",
  best_snapshot_url: "Snapshot Image",
  best_snapshot_path: "Best Snapshot Path",
  plate_image_url: "Plate Image",
  plate_image_path: "Plate Image Path",
  snapshot_url: "Snapshot Image URL",
  snapshot_path: "Snapshot Path",
  plate_secondary_url: "Plate Image URL",
  plate_secondary_path: "Plate Image Path",
  overview_url: "Overview Image URL",
  overview_path: "Overview Path",
  plate_b64_len: "Plate Bytes",
  overview_b64_len: "Overview Bytes",
  plate_secondary_b64_len: "Secondary Plate Bytes",
  coba_classification: "COBA Class",
  coba_confidence: "COBA Confidence",
  simplified_coba_classification: "Simple COBA",
  session_idx: "Session #",
  speed_mps_avg: "Avg Speed (m/s)",
  heading_deg_avg: "Avg Heading (deg)",
  lat_first: "Lat First",
  lon_first: "Lon First",
  lat_last: "Lat Last",
  lon_last: "Lon Last",
  camera_device_id: "Camera Device ID",
  first_seen_at_utc: "First Seen",
  last_seen_at_utc: "Last Seen",
  dwell_seconds: "Dwell (s)",
  dwell_minutes: "Dwell (min)",
  max_confidence_pct: "Max Confidence",
  avg_confidence_pct: "Avg Confidence",
  towards_count: "Towards Count",
  away_count: "Away Count",
  first_motion: "First Motion",
  last_motion: "Last Motion",
  movement_pattern: "Movement Pattern",
  number_of_sessions: "Number of Sessions",
  number_of_unique_vehicles: "Number of Unique Vehicles",
  shortest_stay_minutes: "Shortest Stay (Minutes)",
  longest_stay_minutes: "Longest Stay (Minutes)",
  average_duration_of_stay_minutes: "Average Duration of Stay (Minutes)",
  vehicles_entered_but_not_seen_exiting_by_end_of_beat: "Vehicles Entered But Not Seen Exiting by End of Beat",
  first_snapshot_url: "First Snapshot",
  last_snapshot_url: "Last Snapshot",
  first_plate_secondary_url: "First Plate Image",
  last_plate_secondary_url: "Last Plate Image",
  first_overview_url: "First Overview Image",
  last_overview_url: "Last Overview Image",
  pairing_id: "Pairing ID",
  pairing_type: "Pairing Type",
  route_code: "Route Code",
  route_label: "Route",
  scenario_code: "Scenario Code",
  scenario_label: "Scenario",
  weaving_confirmed: "Weaving Confirmed",
  start_label: "Start Label",
  end_label: "End Label",
  start_sighting_id: "Start Sighting ID",
  end_sighting_id: "End Sighting ID",
  start_time: "Start Time",
  end_time: "End Time",
  first_event_at: "First Event",
  last_event_at: "Last Event",
  start_camera_name: "Start Camera",
  end_camera_name: "End Camera",
  start_camera_device_id: "Start Camera Device ID",
  end_camera_device_id: "End Camera Device ID",
  movement_direction: "Movement Direction",
  journey_status: "Journey Status",
  journey_seconds: "Journey Time (s)",
  journey_minutes: "Journey Time (min)",
  start_camera_lat: "Start Lat",
  start_camera_lon: "Start Lon",
  end_camera_lat: "End Lat",
  end_camera_lon: "End Lon",
  distance_meters: "Distance (m)",
  travel_seconds: "Travel Time (s)",
  travel_minutes: "Travel Time (min)",
  avg_travel_seconds: "Avg Travel Time (s)",
  avg_travel_minutes: "Avg Travel Time (min)",
  estimated_avg_speed_mps: "Avg Speed (m/s)",
  estimated_avg_speed_kph: "Avg Speed (kph)",
  estimated_avg_speed_mph: "Avg Speed (mph)",
  avg_speed_kph: "Avg Speed (kph)",
  avg_speed_mph: "Avg Speed (mph)",
  min_speed_kph: "Min Speed (kph)",
  max_speed_kph: "Max Speed (kph)",
  min_travel_seconds: "Min Travel Window (s)",
  weaving_max_seconds: "Weaving Window Max (s)",
  max_travel_seconds: "Max Travel Window (s)",
  start_snapshot_url: "Start Snapshot",
  end_snapshot_url: "End Snapshot",
  start_plate_secondary_url: "Start Plate Image",
  start_overview_url: "Start Overview Image",
  end_plate_secondary_url: "End Plate Image",
  end_overview_url: "End Overview Image",
  scenario_count: "Count",
  active_from: "Active From",
  active_to: "Active To",
};

const DEFAULT_UNCHECKED_BY_REPORT = {
  "ANPR Plate Sessions": ["avg_confidence_pct", "best_snapshot_path", "plate_image_url"],
  "ANPR Plate Sessions - Time Slots": ["avg_confidence_pct", "best_snapshot_path", "plate_image_url"],
  "ANPR Plate Activity Summary": ["avg_confidence_pct", "best_snapshot_path", "plate_image_url"],
  "ANPR Plate Activity Summary - Time Slots": ["avg_confidence_pct", "best_snapshot_path", "plate_image_url"],
};

const normName = (s) => String(s ?? "").trim();
const safeArray = (v) => (Array.isArray(v) ? v : []);

const getDisplayReportName = (reportOrName) => {
  const rawName =
    typeof reportOrName === "string"
      ? reportOrName
      : String(reportOrName?.display_name || reportOrName?.name || "").trim();

  return REPORT_NAME_DISPLAY_ALIASES[rawName] || rawName;
};

const isTimeSlotReport = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return TIME_SLOT_REPORT_NAMES.has(rawName) || TIME_SLOT_REPORT_NAMES.has(displayName);
};

const inferCategory = (reportName) => {
  const n = normName(reportName).toLowerCase();

  if (n.startsWith("sales ")) return "Sales";
  if (n.startsWith("opportunit")) return "Sales";
  if (n.includes("pipeline")) return "Sales";
  if (n.includes("outcome")) return "Sales";

  if (n.startsWith("jobs ")) return "Operations";
  if (n.includes("job assignment")) return "Operations";
  if (n.includes("classification")) return "Operations";
  if (n.includes("taxonomy")) return "Operations";

  if (n.startsWith("system ")) return "System";
  if (n.startsWith("alerts ")) return "System";
  if (n.startsWith("users ")) return "System";
  if (n.includes("metrics")) return "System";
  if (n.includes("account activity")) return "System";

  if (n.startsWith("portal ")) return "Portal & Messaging";
  if (n.includes("conversation")) return "Portal & Messaging";
  if (n.includes("message")) return "Portal & Messaging";

  if (n.startsWith("anpr ")) return "ANPR";
  if (n.includes("plate")) return "ANPR";
  if (n.includes("weaving")) return "ANPR";
  if (n.includes("movement")) return "ANPR";
  if (n.includes("scenario")) return "ANPR";
  if (n.includes("speed")) return "ANPR";
  if (n.includes("route")) return "ANPR";

  return "Other";
};

const toNiceDate = (d) => {
  try {
    if (!d) return "";
    return format(d, "yyyy-MM-dd");
  } catch {
    return "";
  }
};

const summarizeFields = (fields) => {
  const arr = safeArray(fields).map(String);
  if (!arr.length) return { top: [], extraCount: 0 };
  const top = arr.slice(0, 4);
  const extraCount = Math.max(0, arr.length - top.length);
  return { top, extraCount };
};

const extractAxiosError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  const detail =
    (data && typeof data === "object" && (data.detail || data.message)) ||
    (typeof data === "string" ? data : null);

  const msg = detail || err?.message || "Request failed";
  return { status, msg, data };
};

const isEffectivelyEmpty = (value) => {
  if (value === null || value === undefined) return true;

  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "" || v === "none" || v === "null" || v === "undefined" || v === "n/a";
  }

  return false;
};

const getFallbackVirtualFieldsForReport = (report) => {
  const name = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return safeArray(
    REPORT_VIRTUAL_FIELDS_FALLBACK[name] || REPORT_VIRTUAL_FIELDS_FALLBACK[displayName]
  ).filter(Boolean);
};

const mergeUniqueFields = (...fieldSets) => {
  const out = [];
  for (const set of fieldSets) {
    for (const field of safeArray(set).map(String)) {
      if (field && !out.includes(field)) {
        out.push(field);
      }
    }
  }
  return out;
};

const mergeReportFields = (report, availableFields = null) => {
  const base = safeArray(report?.fields).map(String);
  const backendAvailable = safeArray(availableFields).map(String);
  const fallbackVirtuals = getFallbackVirtualFieldsForReport(report).map(String);

  if (backendAvailable.length) {
    return mergeUniqueFields(backendAvailable, fallbackVirtuals);
  }

  return mergeUniqueFields(base, fallbackVirtuals);
};

const getReportSource = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return REPORT_VIEW_SOURCES[rawName] || REPORT_VIEW_SOURCES[displayName] || "";
};

const isAnprDurationStaySingleCameraReport = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return (
    ANPR_DURATION_STAY_SINGLE_CAMERA_REPORT_NAMES.has(rawName) ||
    ANPR_DURATION_STAY_SINGLE_CAMERA_REPORT_NAMES.has(displayName)
  );
};

const isAnprDurationStaySingleCameraSummaryReport = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return (
    ANPR_DURATION_STAY_SINGLE_CAMERA_SUMMARY_REPORT_NAMES.has(rawName) ||
    ANPR_DURATION_STAY_SINGLE_CAMERA_SUMMARY_REPORT_NAMES.has(displayName)
  );
};

const isAnprJourneyTimeCorridorReport = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return (
    ANPR_JOURNEY_TIME_CORRIDOR_REPORT_NAMES.has(rawName) ||
    ANPR_JOURNEY_TIME_CORRIDOR_REPORT_NAMES.has(displayName)
  );
};

const isAnprSightingsPairingsReport = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return (
    ANPR_SIGHTINGS_PAIRINGS_REPORT_NAMES.has(rawName) ||
    ANPR_SIGHTINGS_PAIRINGS_REPORT_NAMES.has(displayName)
  );
};

const buildInitialReportParameters = (report) => {
  const next = {
    ...EMPTY_REPORT_PARAMETERS,
  };

  if (!isAnprSightingsPairingsReport(report)) {
    return next;
  }

  const settings =
    report?.settings && typeof report.settings === "object"
      ? report.settings
      : {};

  const movement =
    settings?.movement && typeof settings.movement === "object"
      ? settings.movement
      : {};

  const minimum = Number(movement.minimum_journey_seconds);
  const maximum = Number(movement.maximum_journey_seconds);

  next.expected_leg_seconds = String(
    Number.isFinite(minimum) && minimum > 0 ? Math.trunc(minimum) : 1
  );
  next.journey_timeout_seconds = String(
    Number.isFinite(maximum) && maximum > 0 && maximum <= 300
      ? Math.trunc(maximum)
      : 300
  );

  return next;
};

const isAnprParameterizedReport = (report) => {
  const rawName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  return ANPR_PARAMETER_REPORTS.has(rawName) || ANPR_PARAMETER_REPORTS.has(displayName);
};

const buildDefaultSelectedFields = (report, availableFields = null) => {
  const allFields = mergeReportFields(report, availableFields);
  if (!allFields.length) return [];

  const reportName = normName(report?.name);
  const displayName = normName(getDisplayReportName(report));
  const unchecked = new Set(
    DEFAULT_UNCHECKED_BY_REPORT[reportName] || DEFAULT_UNCHECKED_BY_REPORT[displayName] || []
  );

  return allFields.filter((field) => !unchecked.has(field));
};

const moveItem = (items, fromIndex, toIndex) => {
  if (
    !Array.isArray(items) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const readLayoutsFromStorage = () => {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeLayoutsToStorage = (layouts) => {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layouts || {}));
  } catch {
    // ignore storage failures
  }
};

const getReportLayoutKey = (report) => {
  const name = normName(report?.name);
  const id = report?.id ?? "noid";
  return `${id}::${name}`;
};

const normalizePersistedFields = (allFields, savedFields) => {
  const all = safeArray(allFields);
  const saved = safeArray(savedFields).filter((f) => all.includes(f));
  return saved.length ? saved : [];
};

const buildDefaultTimeSlotsForReport = (report) =>
  DEFAULT_REPORT_TIME_SLOTS.map((slot) => ({
    ...slot,
    enabled:
      isAnprSightingsPairingsReport(report) && slot.key === "lunch"
        ? false
        : slot.enabled,
  }));

const cloneDefaultTimeSlots = (report = null) =>
  buildDefaultTimeSlotsForReport(report).map((slot) => ({
    ...slot,
  }));

const normalizePersistedReportParameters = (report, savedParameters) => {
  const defaults = buildInitialReportParameters(report);
  const saved =
    savedParameters && typeof savedParameters === "object"
      ? savedParameters
      : {};

  const next = { ...defaults };

  Object.keys(EMPTY_REPORT_PARAMETERS).forEach((key) => {
    if (saved[key] !== undefined && saved[key] !== null) {
      next[key] = String(saved[key]);
    }
  });

  return next;
};

const normalizePersistedTimeSlots = (report, savedSlots) => {
  const defaults = buildDefaultTimeSlotsForReport(report);
  const savedByKey = new Map(
    safeArray(savedSlots)
      .filter((slot) => slot && typeof slot === "object" && slot.key)
      .map((slot) => [String(slot.key), slot])
  );

  return defaults.map((slot) => {
    const saved = savedByKey.get(slot.key);
    if (!saved) return { ...slot };

    return {
      ...slot,
      label:
        typeof saved.label === "string" && saved.label.trim()
          ? saved.label
          : slot.label,
      start_time:
        typeof saved.start_time === "string" && saved.start_time
          ? saved.start_time
          : slot.start_time,
      end_time:
        typeof saved.end_time === "string" && saved.end_time
          ? saved.end_time
          : slot.end_time,
      enabled:
        typeof saved.enabled === "boolean"
          ? saved.enabled
          : slot.enabled,
    };
  });
};

const buildConfigStateFromReport = (
  report,
  startDateFallback,
  endDateFallback,
  availableFields = null
) => {
  const defaults = {
    selectedFields: buildDefaultSelectedFields(report, availableFields),
    startDate: startDateFallback,
    endDate: endDateFallback,
    autoUncheckEmptyColumns: true,
    reportParameters: buildInitialReportParameters(report),
    reportTimeSlots: cloneDefaultTimeSlots(report),
    timeStart: "",
    timeEnd: "",
  };

  try {
    const layouts = readLayoutsFromStorage();
    const saved = layouts[getReportLayoutKey(report)];
    if (!saved || typeof saved !== "object") {
      return defaults;
    }

    const allFields = mergeReportFields(report, availableFields);
    const selectedFields = normalizePersistedFields(allFields, saved.selectedFields);
    const parsedStartDate = saved.startDate ? new Date(saved.startDate) : null;
    const parsedEndDate = saved.endDate ? new Date(saved.endDate) : null;

    return {
      selectedFields: selectedFields.length ? selectedFields : defaults.selectedFields,
      startDate:
        parsedStartDate && !Number.isNaN(parsedStartDate.getTime())
          ? parsedStartDate
          : defaults.startDate,
      endDate:
        parsedEndDate && !Number.isNaN(parsedEndDate.getTime())
          ? parsedEndDate
          : defaults.endDate,
      autoUncheckEmptyColumns:
        typeof saved.autoUncheckEmptyColumns === "boolean"
          ? saved.autoUncheckEmptyColumns
          : defaults.autoUncheckEmptyColumns,
      reportParameters: normalizePersistedReportParameters(
        report,
        saved.reportParameters
      ),
      reportTimeSlots: normalizePersistedTimeSlots(
        report,
        saved.reportTimeSlots
      ),
      timeStart:
        typeof saved.timeStart === "string" ? saved.timeStart : defaults.timeStart,
      timeEnd:
        typeof saved.timeEnd === "string" ? saved.timeEnd : defaults.timeEnd,
    };
  } catch {
    return defaults;
  }
};

const StandardReportsWidget = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const { triggerAlert } = useAlerts();
  const { theme } = useTheme();
  const { currentUser } = useAuth();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);

  const [selectedReport, setSelectedReport] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configReport, setConfigReport] = useState(null);
  const [configAvailableFields, setConfigAvailableFields] = useState([]);
  const [configLoadingFields, setConfigLoadingFields] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [autoUncheckEmptyColumns, setAutoUncheckEmptyColumns] = useState(true);

  const [startDate, setStartDate] = useState(new Date("2025-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");

  const [reportParameters, setReportParameters] = useState(() => buildInitialReportParameters(null));
  const [reportTimeSlots, setReportTimeSlots] = useState(cloneDefaultTimeSlots);
  const [anprJobOptions, setAnprJobOptions] = useState([]);
  const [parameterRouteOptions, setParameterRouteOptions] = useState([]);
  const [parameterPairingOptions, setParameterPairingOptions] = useState([]);
  const [parameterBoundCameraOptions, setParameterBoundCameraOptions] = useState([]);
  const [loadingParameterJobs, setLoadingParameterJobs] = useState(false);
  const [loadingParameterRoutes, setLoadingParameterRoutes] = useState(false);
  const [loadingParameterCameras, setLoadingParameterCameras] = useState(false);

  const [openGroups, setOpenGroups] = useState(() => ({
    Sales: true,
    Operations: true,
    System: false,
    "Portal & Messaging": false,
    ANPR: true,
    Other: false,
  }));

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/reports/templates");
      const std = safeArray(res?.data)
        .filter((r) => !!r?.is_standard)
        .map((r) => ({
          ...r,
          display_name: getDisplayReportName(r),
          fields: mergeReportFields(r),
        }));

      std.sort((a, b) => {
        const ca = inferCategory(a?.display_name || a?.name);
        const cb = inferCategory(b?.display_name || b?.name);
        if (ca !== cb) return ca.localeCompare(cb);
        return normName(a?.display_name || a?.name).localeCompare(
          normName(b?.display_name || b?.name)
        );
      });

      setReports(std);
    } catch (err) {
      const e = extractAxiosError(err);
      console.error("Failed to load reports:", err, e);
      triggerAlert(
        `Failed to load reports${e.status ? ` (${e.status})` : ""}: ${e.msg}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const out = new Map();
    for (const r of reports) {
      const cat = inferCategory(r?.display_name || r?.name);
      if (!out.has(cat)) out.set(cat, []);
      out.get(cat).push(r);
    }

    const order = ["Sales", "Operations", "System", "Portal & Messaging", "ANPR", "Other"];
    const ordered = [];
    for (const k of order) {
      if (out.has(k)) ordered.push([k, out.get(k)]);
    }
    for (const [k, v] of out.entries()) {
      if (!order.includes(k)) ordered.push([k, v]);
    }
    return ordered;
  }, [reports]);

  const configAllFields = useMemo(
    () => mergeReportFields(configReport, configAvailableFields),
    [configReport, configAvailableFields]
  );

  const selectedFieldsSet = useMemo(() => new Set(selectedFields), [selectedFields]);

  const availableUnselectedFields = useMemo(
    () => configAllFields.filter((field) => !selectedFieldsSet.has(field)),
    [configAllFields, selectedFieldsSet]
  );

  const configReportSource = useMemo(() => getReportSource(configReport), [configReport]);

  const configUsesParameters = useMemo(
    () => isAnprParameterizedReport(configReport),
    [configReport]
  );

  const configUsesDurationStaySingleCamera = useMemo(
    () => isAnprDurationStaySingleCameraReport(configReport),
    [configReport]
  );

  const configUsesDurationStaySingleCameraSummary = useMemo(
    () => isAnprDurationStaySingleCameraSummaryReport(configReport),
    [configReport]
  );

  const configUsesJourneyTimeCorridor = useMemo(
    () => isAnprJourneyTimeCorridorReport(configReport),
    [configReport]
  );

  const configUsesSightingsPairings = useMemo(
    () => isAnprSightingsPairingsReport(configReport),
    [configReport]
  );

  const parameterCameraOptions = useMemo(() => {
    const cameras = new Map();

    parameterBoundCameraOptions.forEach((camera) => {
      const id = camera?.camera_device_id ?? camera?.id;
      if (id === undefined || id === null || id === "") return;
      const key = String(id);
      cameras.set(key, {
        id: key,
        name: camera?.camera_name || camera?.name || `Camera ${key}`,
        binding_id: camera?.binding_id ?? null,
      });
    });

    return Array.from(cameras.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" })
    );
  }, [parameterBoundCameraOptions]);

  const selectedJourneyPairing = useMemo(() => {
    if (!reportParameters.pairing_id) return null;
    const selectedId = String(reportParameters.pairing_id);
    return parameterPairingOptions.find((pairing) => String(pairing?.id) === selectedId) || null;
  }, [parameterPairingOptions, reportParameters.pairing_id]);

  const configUsesTimeSlots = useMemo(
    () => isTimeSlotReport(configReport),
    [configReport]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const naturalContentHeight = Math.ceil(
          Math.max(
            content.getBoundingClientRect().height || 0,
            content.scrollHeight || 0
          )
        );

        root.style.height = "auto";
        root.style.minHeight =
          Number.isFinite(naturalContentHeight) && naturalContentHeight > 0
            ? `${naturalContentHeight}px`
            : "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8)
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    loading,
    openGroups,
    reports.length,
  ]);

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const fetchAvailableFieldsForReport = async (report) => {
    if (!report?.id) {
      return mergeReportFields(report);
    }

    try {
      const res = await axiosInstance.get(`/api/reports/templates/${report.id}/available-fields`);
      const backendFields = safeArray(res?.data?.fields).map(String);
      return mergeReportFields(report, backendFields);
    } catch (err) {
      const e = extractAxiosError(err);
      console.error("Failed to load available fields:", err, e);
      triggerAlert(
        `Failed to load live fields${e.status ? ` (${e.status})` : ""}: ${e.msg}`,
        "warning"
      );
      return mergeReportFields(report);
    }
  };

  const loadAnprJobOptionsForParameters = async () => {
    setLoadingParameterJobs(true);
    try {
      const res = await axiosInstance.get(
        `/api/anpr/cameras/jobs/anpr-options?limit=${JOB_OPTIONS_LIMIT}`
      );
      setAnprJobOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const e = extractAxiosError(err);
      console.error("Failed to load ANPR job parameter options:", err, e);
      triggerAlert(
        `Failed to load ANPR jobs${e.status ? ` (${e.status})` : ""}: ${e.msg}`,
        "warning"
      );
      setAnprJobOptions([]);
    } finally {
      setLoadingParameterJobs(false);
    }
  };

  const loadBoundCameraOptionsForJob = async (jobId) => {
    if (!jobId) {
      setParameterBoundCameraOptions([]);
      return;
    }

    setLoadingParameterCameras(true);
    try {
      const res = await axiosInstance.get(
        `/api/anpr/cameras/jobs/${encodeURIComponent(jobId)}/bound-cameras`
      );
      setParameterBoundCameraOptions(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      const e = extractAxiosError(err);
      console.error("Failed to load job-bound ANPR cameras:", err, e);
      triggerAlert(
        `Failed to load bound cameras${e.status ? ` (${e.status})` : ""}: ${e.msg}`,
        "warning"
      );
      setParameterBoundCameraOptions([]);
    } finally {
      setLoadingParameterCameras(false);
    }
  };

  const loadRouteAndPairingOptionsForJob = async (jobId) => {
    if (!jobId) {
      setParameterRouteOptions([]);
      setParameterPairingOptions([]);
      return;
    }

    setLoadingParameterRoutes(true);

    const [routesResult, pairingsResult] = await Promise.allSettled([
      axiosInstance.get(`/api/reports/weaving/routes?job_id=${encodeURIComponent(jobId)}`),
      axiosInstance.get(
        `/api/anpr/cameras/pairings?job_id=${encodeURIComponent(jobId)}&include_inactive=true`
      ),
    ]);

    const routeRows =
      routesResult.status === "fulfilled" && Array.isArray(routesResult.value?.data)
        ? routesResult.value.data
        : [];

    const pairingRows =
      pairingsResult.status === "fulfilled" && Array.isArray(pairingsResult.value?.data)
        ? pairingsResult.value.data
        : [];

    if (routesResult.status === "rejected") {
      const e = extractAxiosError(routesResult.reason);
      console.error("Failed to load ANPR route options:", routesResult.reason, e);
      triggerAlert(
        `Failed to load ANPR routes${e.status ? ` (${e.status})` : ""}: ${e.msg}`,
        "warning"
      );
    }

    if (pairingsResult.status === "rejected") {
      const e = extractAxiosError(pairingsResult.reason);
      console.error("Failed to load ANPR pairing options:", pairingsResult.reason, e);
      triggerAlert(
        `Failed to load ANPR pairings${e.status ? ` (${e.status})` : ""}: ${e.msg}`,
        "warning"
      );
    }

    const routeMap = new Map();

    pairingRows.forEach((pairing) => {
      if (pairing?.route_code) {
        routeMap.set(pairing.route_code, pairing.route_label || pairing.route_code);
      }
    });

    routeRows.forEach((route) => {
      if (route?.route_code) {
        routeMap.set(route.route_code, route.route_label || route.route_code);
      }
    });

    setParameterRouteOptions(
      Array.from(routeMap.entries()).map(([code, label]) => ({
        route_code: code,
        route_label: label,
      }))
    );
    setParameterPairingOptions(pairingRows);
    setLoadingParameterRoutes(false);
  };

  const openConfig = async (report) => {
    setConfigLoadingFields(true);
    setReportParameters(buildInitialReportParameters(report));
    setReportTimeSlots(cloneDefaultTimeSlots(report));
    setParameterRouteOptions([]);
    setParameterPairingOptions([]);
    setParameterBoundCameraOptions([]);

    const liveFields = await fetchAvailableFieldsForReport(report);

    const enrichedReport = {
      ...report,
      display_name: getDisplayReportName(report),
      fields: liveFields,
    };

    const persisted = buildConfigStateFromReport(
      enrichedReport,
      startDate,
      endDate,
      liveFields
    );

    setConfigReport(enrichedReport);
    setConfigAvailableFields(liveFields);
    setSelectedFields(persisted.selectedFields);
    setStartDate(persisted.startDate);
    setEndDate(persisted.endDate);
    setAutoUncheckEmptyColumns(persisted.autoUncheckEmptyColumns);
    setReportParameters(persisted.reportParameters);
    setReportTimeSlots(persisted.reportTimeSlots);
    setTimeStart(persisted.timeStart);
    setTimeEnd(persisted.timeEnd);

    if (isAnprParameterizedReport(enrichedReport)) {
      await loadAnprJobOptionsForParameters();

      if (persisted.reportParameters.job_id) {
        if (isAnprDurationStaySingleCameraReport(enrichedReport)) {
          await loadBoundCameraOptionsForJob(persisted.reportParameters.job_id);
        } else if (!isAnprSightingsPairingsReport(enrichedReport)) {
          await loadRouteAndPairingOptionsForJob(persisted.reportParameters.job_id);
        }
      }
    }

    setShowConfigModal(true);
    setConfigLoadingFields(false);
  };

  const closeConfig = () => {
    setShowConfigModal(false);
    setConfigReport(null);
    setConfigAvailableFields([]);
    setSelectedFields([]);
    setAutoUncheckEmptyColumns(true);
    setConfigLoadingFields(false);
    setReportParameters(buildInitialReportParameters(null));
    setReportTimeSlots(cloneDefaultTimeSlots(null));
    setTimeStart("");
    setTimeEnd("");
    setParameterRouteOptions([]);
    setParameterPairingOptions([]);
    setParameterBoundCameraOptions([]);
    setLoadingParameterJobs(false);
    setLoadingParameterRoutes(false);
    setLoadingParameterCameras(false);
  };

  const updateReportParameter = async (field, value) => {
    setReportParameters((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "job_id") {
        next.route_code = "";
        next.scenario_code = "";
        next.pairing_id = "";
        next.camera_device_id = "";
        next.camera_name = "";
      }

      if (field === "route_code") {
        next.pairing_id = "";
        next.start_camera_device_id = "";
        next.start_camera_name = "";
        next.end_camera_device_id = "";
        next.end_camera_name = "";
      }

      if (field === "pairing_id") {
        const selected = parameterPairingOptions.find((pairing) => String(pairing?.id) === String(value));
        if (selected) {
          next.route_code = selected.route_code || next.route_code || "";
          next.start_camera_device_id = String(
            selected.start_camera_device_id ?? selected.start_camera_id ?? ""
          );
          next.start_camera_name = selected.start_camera_name || "";
          next.end_camera_device_id = String(
            selected.end_camera_device_id ?? selected.end_camera_id ?? ""
          );
          next.end_camera_name = selected.end_camera_name || "";
        } else {
          next.start_camera_device_id = "";
          next.start_camera_name = "";
          next.end_camera_device_id = "";
          next.end_camera_name = "";
        }
      }

      return next;
    });

    if (field === "job_id") {
      if (configUsesDurationStaySingleCamera) {
        await loadBoundCameraOptionsForJob(value);
      } else if (!configUsesSightingsPairings) {
        await loadRouteAndPairingOptionsForJob(value);
      }
    }
  };

  const updateTimeSlot = (key, field, value) => {
    setReportTimeSlots((prev) =>
      prev.map((slot) =>
        slot.key === key
          ? {
              ...slot,
              [field]: value,
            }
          : slot
      )
    );
  };

  const toggleTimeSlot = (key) => {
    setReportTimeSlots((prev) =>
      prev.map((slot) =>
        slot.key === key
          ? {
              ...slot,
              enabled: !slot.enabled,
            }
          : slot
      )
    );
  };

  const resetTimeSlots = () => {
    setReportTimeSlots(cloneDefaultTimeSlots(configReport));
  };

  const buildTimeSlotPayload = () => {
    if (!configUsesTimeSlots) return {};

    const enabledSlots = reportTimeSlots
      .filter((slot) => slot.enabled)
      .map((slot) => ({
        label: String(slot.label || "").trim() || null,
        start_time: slot.start_time,
        end_time: slot.end_time,
      }));

    return {
      time_slots: enabledSlots,
      apply_pairing_rules: true,
      exclude_invalid_pairings: true,
    };
  };

  const validateTimeSlots = () => {
    if (!configUsesTimeSlots) return true;

    const enabledSlots = reportTimeSlots.filter((slot) => slot.enabled);

    if (!enabledSlots.length) {
      triggerAlert("Enable at least one time slot for this report", "warning");
      return false;
    }

    for (const slot of enabledSlots) {
      if (!slot.start_time || !slot.end_time) {
        triggerAlert(`Time slot "${slot.label || slot.key}" needs both start and end time`, "warning");
        return false;
      }

      if (slot.start_time >= slot.end_time) {
        triggerAlert(
          `Time slot "${slot.label || slot.key}" has an invalid range. Start must be before end.`,
          "warning"
        );
        return false;
      }
    }

    return true;
  };

  const validateSingleTimeWindow = () => {
    if (configUsesTimeSlots) return true;

    const hasStart = !!timeStart;
    const hasEnd = !!timeEnd;

    if (hasStart !== hasEnd) {
      triggerAlert("Use both Start Time and End Time, or leave both blank.", "warning");
      return false;
    }

    if (hasStart && hasEnd && timeStart >= timeEnd) {
      triggerAlert("Start Time must be before End Time.", "warning");
      return false;
    }

    return true;
  };

  const buildSingleTimeWindowPayload = () => {
    if (configUsesTimeSlots || !timeStart || !timeEnd) return {};

    return {
      time_start: timeStart,
      time_end: timeEnd,
    };
  };

  const toggleField = (field) => {
    setSelectedFields((prev) => {
      if (prev.includes(field)) {
        return prev.filter((f) => f !== field);
      }
      return [...prev, field];
    });
  };

  const moveSelectedFieldUp = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx <= 0) return prev;
      return moveItem(prev, idx, idx - 1);
    });
  };

  const moveSelectedFieldDown = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      return moveItem(prev, idx, idx + 1);
    });
  };

  const moveSelectedFieldToTop = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx <= 0) return prev;
      return moveItem(prev, idx, 0);
    });
  };

  const moveSelectedFieldToBottom = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx < 0 || idx === prev.length - 1) return prev;
      return moveItem(prev, idx, prev.length - 1);
    });
  };

  const selectAllFields = () => {
    setSelectedFields(configAllFields);
  };

  const clearAllFields = () => {
    setSelectedFields([]);
  };

  const resetDefaultFields = () => {
    setSelectedFields(buildDefaultSelectedFields(configReport, configAvailableFields));
  };

  const removeEmptyColumnsFromData = (rows, fields) => {
    return fields.filter((field) => rows.some((row) => !isEffectivelyEmpty(row?.[field])));
  };

  const saveCurrentLayoutToStorage = (
    report,
    fields,
    sd,
    ed,
    autoFlag,
    parameters,
    timeSlots,
    savedTimeStart,
    savedTimeEnd
  ) => {
    if (!report) return;

    const layouts = readLayoutsFromStorage();
    layouts[getReportLayoutKey(report)] = {
      selectedFields: safeArray(fields),
      startDate: sd ? new Date(sd).toISOString() : null,
      endDate: ed ? new Date(ed).toISOString() : null,
      autoUncheckEmptyColumns: !!autoFlag,
      reportParameters: {
        ...buildInitialReportParameters(report),
        ...(parameters && typeof parameters === "object" ? parameters : {}),
      },
      reportTimeSlots: safeArray(timeSlots).map((slot) => ({
        key: slot.key,
        label: slot.label,
        start_time: slot.start_time,
        end_time: slot.end_time,
        enabled: !!slot.enabled,
      })),
      timeStart: typeof savedTimeStart === "string" ? savedTimeStart : "",
      timeEnd: typeof savedTimeEnd === "string" ? savedTimeEnd : "",
      savedAt: new Date().toISOString(),
    };
    writeLayoutsToStorage(layouts);
  };

  const handleSaveLayout = () => {
    if (!configReport) return;

    if (!selectedFields.length) {
      triggerAlert("Select at least one field before saving layout", "warning");
      return;
    }

    saveCurrentLayoutToStorage(
      configReport,
      selectedFields,
      startDate,
      endDate,
      autoUncheckEmptyColumns,
      reportParameters,
      reportTimeSlots,
      timeStart,
      timeEnd
    );
    triggerAlert("Report layout and filters saved", "success");
  };

  const getRoleMeta = () => {
    const roleValue =
      currentUser?.role_id ??
      currentUser?.role?.id ??
      currentUser?.role ??
      currentUser?.role_name ??
      null;

    const roleName = String(
      currentUser?.role?.name ?? currentUser?.role_name ?? currentUser?.role ?? ""
    )
      .trim()
      .toLowerCase();

    const roleIdNum = Number(roleValue);

    const isAdminLikeByName =
      roleName.includes("super admin") ||
      roleName.includes("system admin") ||
      roleName === "admin" ||
      roleName.includes("manager");

    const isAdminLikeById = [1, 2, 3, 4].includes(roleIdNum);

    return {
      canUpdateTemplate: isAdminLikeByName || isAdminLikeById,
    };
  };

  const { canUpdateTemplate } = getRoleMeta();

  const handleUpdateTemplate = () => {
    triggerAlert(
      "Update Template needs a backend template-update route before it can rewrite the DB template.",
      "warning"
    );
  };

  const buildReportParametersPayload = () => {
    if (!configUsesParameters) return {};

    const params = {};

    if (reportParameters.job_id) {
      params.job_id = Number(reportParameters.job_id);
    }

    if (reportParameters.route_code) {
      params.route_code = reportParameters.route_code;
    }

    if (reportParameters.scenario_code) {
      params.scenario_code =
        reportParameters.scenario_code === "invalid_too_fast"
          ? "below_min_travel_time"
          : reportParameters.scenario_code;
    }

    if (reportParameters.pairing_id) {
      params.pairing_id = Number(reportParameters.pairing_id);
    }

    if (configUsesDurationStaySingleCamera) {
      if (reportParameters.camera_device_id) {
        params.camera_device_id = Number(reportParameters.camera_device_id);
      }

      if (reportParameters.camera_name) {
        params.camera_name = reportParameters.camera_name;
      }

      params.entry_motion = reportParameters.entry_motion || "towards";
      params.exit_motion = (reportParameters.entry_motion || "towards") === "towards" ? "away" : "towards";

      if (!configUsesDurationStaySingleCameraSummary && reportParameters.movement_pattern) {
        params.movement_pattern = reportParameters.movement_pattern;
      }

      if (reportParameters.min_dwell_minutes !== "") {
        params.min_dwell_minutes = Number(reportParameters.min_dwell_minutes);
      }

      if (reportParameters.max_dwell_minutes !== "") {
        params.max_dwell_minutes = Number(reportParameters.max_dwell_minutes);
      }
    }

    if (configUsesJourneyTimeCorridor) {
      if (reportParameters.start_camera_device_id) {
        params.start_camera_device_id = Number(reportParameters.start_camera_device_id);
      }

      if (reportParameters.end_camera_device_id) {
        params.end_camera_device_id = Number(reportParameters.end_camera_device_id);
      }

      if (reportParameters.start_camera_name) {
        params.start_camera_name = reportParameters.start_camera_name;
      }

      if (reportParameters.end_camera_name) {
        params.end_camera_name = reportParameters.end_camera_name;
      }

      if (reportParameters.movement_direction) {
        params.movement_direction = reportParameters.movement_direction;
      }

      if (reportParameters.journey_status) {
        params.journey_status = reportParameters.journey_status;
      }

      if (reportParameters.scenario_code) {
        params.scenario_code = reportParameters.scenario_code;
      }

      if (reportParameters.min_journey_minutes !== "") {
        params.min_journey_minutes = Number(reportParameters.min_journey_minutes);
      }

      if (reportParameters.max_journey_minutes !== "") {
        params.max_journey_minutes = Number(reportParameters.max_journey_minutes);
      }
    }

    if (configUsesSightingsPairings) {
      if (reportParameters.expected_leg_seconds !== "") {
        params.expected_leg_seconds = Number(reportParameters.expected_leg_seconds);
      }

      if (reportParameters.journey_timeout_seconds !== "") {
        params.journey_timeout_seconds = Number(reportParameters.journey_timeout_seconds);
      }
    }

    return params;
  };

  const handlePreviewConfigured = async () => {
    if (!configReport) return;

    let fields = safeArray(selectedFields);
    if (!fields.length) {
      triggerAlert("Select at least one field", "warning");
      return;
    }

    const sd = toNiceDate(startDate);
    const ed = toNiceDate(endDate);

    if (!sd || !ed) {
      triggerAlert("Invalid date range", "warning");
      return;
    }

    if (new Date(sd) > new Date(ed)) {
      triggerAlert("Start date cannot be after end date", "warning");
      return;
    }

    if (!validateTimeSlots()) {
      return;
    }

    if (!validateSingleTimeWindow()) {
      return;
    }

    const parameters = buildReportParametersPayload();

    if (configUsesDurationStaySingleCamera) {
      if (!parameters.camera_device_id && !parameters.camera_name) {
        triggerAlert("Select a camera before previewing this report", "warning");
        return;
      }

      const minDwell = Number(reportParameters.min_dwell_minutes);
      const maxDwell = Number(reportParameters.max_dwell_minutes);

      if (!Number.isFinite(minDwell) || minDwell < 0) {
        triggerAlert("Minimum dwell minutes must be zero or greater", "warning");
        return;
      }

      if (!Number.isFinite(maxDwell) || maxDwell <= 0) {
        triggerAlert("Maximum dwell minutes must be greater than zero", "warning");
        return;
      }

      if (minDwell > maxDwell) {
        triggerAlert("Minimum dwell minutes cannot be greater than maximum dwell minutes", "warning");
        return;
      }
    }

    if (configUsesJourneyTimeCorridor) {
      if (!parameters.pairing_id) {
        triggerAlert("Select a camera pairing before previewing this report", "warning");
        return;
      }

      if (!selectedJourneyPairing) {
        triggerAlert("Selected camera pairing could not be found. Re-select the pairing.", "warning");
        return;
      }

      const pairingDistance = Number(selectedJourneyPairing?.distance_meters);
      if (!Number.isFinite(pairingDistance) || pairingDistance <= 0) {
        triggerAlert("Selected camera pairing has no valid distance. Set distance_meters in the pairing tool before running Journey Time.", "warning");
        return;
      }

      const rangeStart = new Date(sd);
      const rangeEnd = new Date(ed);
      const dayMs = 24 * 60 * 60 * 1000;
      const selectedDays = Math.floor((rangeEnd - rangeStart) / dayMs) + 1;
      if (selectedDays > 31) {
        triggerAlert("Journey Time reports must be limited to 31 days or less. Select the actual survey/beat date range.", "warning");
        return;
      }

      const minJourney = Number(reportParameters.min_journey_minutes);
      const maxJourney = Number(reportParameters.max_journey_minutes);

      if (!Number.isFinite(minJourney) || minJourney < 0) {
        triggerAlert("Minimum journey minutes must be zero or greater", "warning");
        return;
      }

      if (!Number.isFinite(maxJourney) || maxJourney <= 0) {
        triggerAlert("Maximum journey minutes must be greater than zero", "warning");
        return;
      }

      if (minJourney > maxJourney) {
        triggerAlert("Minimum journey minutes cannot be greater than maximum journey minutes", "warning");
        return;
      }
    }

    if (configUsesSightingsPairings) {
      const minimumJourneySeconds = Number(reportParameters.expected_leg_seconds);
      const maximumJourneySeconds = Number(reportParameters.journey_timeout_seconds);

      if (!Number.isFinite(minimumJourneySeconds) || minimumJourneySeconds < 1) {
        triggerAlert("Minimum logical journey must be at least 1 second", "warning");
        return;
      }

      if (!Number.isFinite(maximumJourneySeconds) || maximumJourneySeconds < 1) {
        triggerAlert("Maximum logical journey must be at least 1 second", "warning");
        return;
      }

      if (maximumJourneySeconds > 300) {
        triggerAlert("Maximum logical journey cannot exceed 300 seconds", "warning");
        return;
      }

      if (minimumJourneySeconds > maximumJourneySeconds) {
        triggerAlert(
          "Minimum logical journey cannot be greater than maximum logical journey",
          "warning"
        );
        return;
      }
    }

    if (configUsesParameters && !parameters.job_id) {
      triggerAlert("Select an ANPR job before previewing this report", "warning");
      return;
    }

    const timePayload = buildTimeSlotPayload();
    const singleTimePayload = buildSingleTimeWindowPayload();

    const previewLimit = configUsesJourneyTimeCorridor ? 5000 : 250;

    const payload = {
      template_id: configReport.id,
      name: configReport.name,
      fields,
      start_date: sd,
      end_date: ed,
      available_to_clients: false,
      preview: true,
      preview_limit: previewLimit,
      parameters,
      ...timePayload,
      ...singleTimePayload,
    };

    try {
      console.log("[StandardReportsWidget] report run payload", payload);

      const res = await axiosInstance.post("/api/reports/run", payload, {
        headers: { "Content-Type": "application/json" },
      });

      console.log("[StandardReportsWidget] report run response", {
        row_count: res?.data?.row_count,
        data_length: Array.isArray(res?.data?.data) ? res.data.data.length : null,
        first_row: Array.isArray(res?.data?.data) ? res.data.data[0] : null,
        response_keys: res?.data && typeof res.data === "object" ? Object.keys(res.data) : [],
      });

      let finalFields = [...fields];

      if (autoUncheckEmptyColumns) {
        const nonEmptyFields = removeEmptyColumnsFromData(safeArray(res?.data?.data), finalFields);
        finalFields = nonEmptyFields.length ? nonEmptyFields : finalFields;
      }

      if (!finalFields.length && safeArray(res?.data?.data).length) {
        finalFields = Object.keys(res.data.data[0] || {});
      }

      const trimmedRows = safeArray(res?.data?.data).map((row) => {
        const trimmed = {};
        finalFields.forEach((f) => {
          trimmed[f] = row?.[f];
        });
        return trimmed;
      });

      console.log("[StandardReportsWidget] preview render state", {
        report_name: configReport.name,
        selected_fields: fields,
        final_fields: finalFields,
        trimmed_rows_length: trimmedRows.length,
        first_trimmed_row: trimmedRows[0] || null,
        auto_uncheck_empty_columns: autoUncheckEmptyColumns,
      });

      setSelectedFields(finalFields);
      setSelectedReport({
        ...configReport,
        display_name: getDisplayReportName(configReport),
        fields: finalFields,
      });
      setPreviewData({
        ...res.data,
        data: trimmedRows,
        parameters,
        time_slots: timePayload.time_slots || [],
        time_start: singleTimePayload.time_start || null,
        time_end: singleTimePayload.time_end || null,
        preview_limit: previewLimit,
        export_payload: {
          ...payload,
          fields: finalFields,
          preview: false,
          preview_limit: null,
        },
      });
      setShowPreviewModal(true);
      setShowConfigModal(false);
    } catch (err) {
      const e = extractAxiosError(err);
      console.error("Preview failed:", err, e);
      const base = `Failed to run report${e.status ? ` (${e.status})` : ""}`;
      triggerAlert(`${base}: ${e.msg}`, "error");
    }
  };

  const primary = theme?.primary_color || "#111827";
  const accent = theme?.accent_color || "#ff7a00";
  const groupBtnColor = "#ffffff";

  const controlBtnClass =
    "min-h-[44px] w-full sm:w-auto bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold py-2 px-3 rounded";
  const previewBtnClass =
    "min-h-[44px] w-full sm:w-auto bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-xs font-semibold py-2 px-3 rounded";

  const orderPillStyle = {
    border: `1px solid ${accent}`,
    color: accent,
    background: "transparent",
    borderRadius: 999,
    padding: "3px 7px",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.1,
    minWidth: 0,
  };

  const disabledOrderPillStyle = {
    ...orderPillStyle,
    opacity: 0.35,
    cursor: "not-allowed",
  };

  const parameterInputClass = "min-h-[44px] rounded border px-2 py-2 text-sm w-full md:min-h-0";

  const timeSlotControls =
    configUsesTimeSlots && configReport ? (
      <div
        className="mb-3 shrink-0 border rounded p-3"
        style={{
          borderColor: "#e5e7eb",
          background: "#fff7ed",
        }}
      >
        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-gray-800">
            ANPR Time Slot Filters
          </div>

          <button type="button" onClick={resetTimeSlots} className={controlBtnClass}>
            Reset Slots
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {reportTimeSlots.map((slot) => (
            <div key={slot.key} className="border rounded p-2 bg-white">
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                <input
                  type="checkbox"
                  checked={!!slot.enabled}
                  onChange={() => toggleTimeSlot(slot.key)}
                />
                Enabled
              </label>

              <label className="block text-xs text-gray-600 mb-1">Label</label>
              <input
                type="text"
                value={slot.label}
                onChange={(e) => updateTimeSlot(slot.key, "label", e.target.value)}
                className={parameterInputClass}
                disabled={!slot.enabled}
              />

              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start</label>
                  <input
                    type="time"
                    value={slot.start_time}
                    onChange={(e) => updateTimeSlot(slot.key, "start_time", e.target.value)}
                    className={parameterInputClass}
                    disabled={!slot.enabled}
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">End</label>
                  <input
                    type="time"
                    value={slot.end_time}
                    onChange={(e) => updateTimeSlot(slot.key, "end_time", e.target.value)}
                    className={parameterInputClass}
                    disabled={!slot.enabled}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-gray-600 mt-2">
          {configUsesSightingsPairings
            ? "Time slots are applied to the report row. Movement matching may look up to 300 seconds before or after that sighting so boundary movements are retained."
            : "Time slots are applied server-side before preview/export. Pairing rules are also enforced for slotted ANPR reports."}
        </div>
      </div>
    ) : null;

  const parameterControls =
    configUsesParameters && configReport ? (
      <div
        className="mb-3 shrink-0 border rounded p-3"
        style={{
          borderColor: "#e5e7eb",
          background: "#fafafa",
        }}
      >
        <div className="text-sm font-semibold text-gray-800 mb-2">
          ANPR Report Parameters
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="min-w-0">
            <label className="block text-xs text-gray-600 mb-1">ANPR Job</label>
            <select
              value={reportParameters.job_id}
              onChange={(e) => updateReportParameter("job_id", e.target.value)}
              className={parameterInputClass}
              disabled={loadingParameterJobs}
            >
              <option value="">
                {loadingParameterJobs ? "Loading ANPR jobs..." : "Select ANPR job"}</option>
              {anprJobOptions.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.job_display || `${job.job_ref || "Job"} (#${job.id})`}
                </option>
              ))}
            </select>
          </div>

          {configUsesSightingsPairings ? (
            <>
              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">
                  Minimum Logical Journey (seconds)
                </label>
                <input
                  type="number"
                  min="1"
                  max="300"
                  step="1"
                  value={reportParameters.expected_leg_seconds}
                  onChange={(e) =>
                    updateReportParameter("expected_leg_seconds", e.target.value)
                  }
                  className={parameterInputClass}
                />
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">
                  Maximum Logical Journey (seconds)
                </label>
                <input
                  type="number"
                  min="1"
                  max="300"
                  step="1"
                  value={reportParameters.journey_timeout_seconds}
                  onChange={(e) =>
                    updateReportParameter("journey_timeout_seconds", e.target.value)
                  }
                  className={parameterInputClass}
                />
              </div>

              <div className="md:col-span-2 text-[11px] text-gray-500 leading-5">
                Logical movements are matched between the minimum and maximum
                journey times. The default window is 1-300 seconds. Different-camera
                reads form directed movements; same-camera opposite-motion reads
                (away ↔ towards) are recorded as U-Turns.
              </div>
            </>
          ) : configUsesJourneyTimeCorridor ? (
            <>
              <div className="min-w-0 md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Camera Pairing</label>
                <select
                  value={reportParameters.pairing_id}
                  onChange={(e) => updateReportParameter("pairing_id", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id || loadingParameterRoutes}
                >
                  <option value="">
                    {!reportParameters.job_id
                      ? "Select job first"
                      : loadingParameterRoutes
                      ? "Loading pairings..."
                      : "Select camera pairing"}
                  </option>
                  {parameterPairingOptions
                    .filter((pairing) => {
                      if (!reportParameters.route_code) return true;
                      return pairing.route_code === reportParameters.route_code;
                    })
                    .map((pairing) => {
                      const startName = pairing.start_camera_name || pairing.start_camera_device_id;
                      const endName = pairing.end_camera_name || pairing.end_camera_device_id;
                      const routeLabel = pairing.route_label || pairing.route_code || `Pairing ${pairing.id}`;
                      return (
                        <option key={pairing.id} value={pairing.id}>
                          {routeLabel} · {startName} → {endName} · {formatPairingDistance(pairing)}
                        </option>
                      );
                    })}
                </select>
                {selectedJourneyPairing ? (
                  <div className="mt-1 text-[11px] text-gray-500">
                    Selected: {selectedJourneyPairing.start_camera_name || selectedJourneyPairing.start_camera_device_id} → {selectedJourneyPairing.end_camera_name || selectedJourneyPairing.end_camera_device_id}
                    {" · "}Distance: {formatPairingDistance(selectedJourneyPairing)}
                    {selectedJourneyPairing.min_travel_seconds || selectedJourneyPairing.max_travel_seconds ? (
                      <>
                        {" · "}Window: {selectedJourneyPairing.min_travel_seconds || 30}s–{selectedJourneyPairing.max_travel_seconds || 3600}s
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Route</label>
                <select
                  value={reportParameters.route_code}
                  onChange={(e) => updateReportParameter("route_code", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id || loadingParameterRoutes}
                >
                  <option value="">All routes</option>
                  {parameterRouteOptions.map((route) => (
                    <option key={route.route_code} value={route.route_code}>
                      {route.route_label || route.route_code}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Scenario</label>
                <select
                  value={reportParameters.scenario_code}
                  onChange={(e) => updateReportParameter("scenario_code", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id}
                >
                  <option value="">All scenarios</option>
                  {JOURNEY_SCENARIO_OPTIONS.map((scenario) => (
                    <option key={scenario.code} value={scenario.code}>
                      {scenario.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Direction</label>
                <select
                  value={reportParameters.movement_direction}
                  onChange={(e) => updateReportParameter("movement_direction", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id}
                >
                  <option value="">All movements</option>
                  <option value="start_to_end">Start → End</option>
                  <option value="end_to_start">End → Start</option>
                  <option value="start_only">Start only / no end capture</option>
                  <option value="end_only">End only / no prior start</option>
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Journey Status</label>
                <select
                  value={reportParameters.journey_status}
                  onChange={(e) => updateReportParameter("journey_status", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id}
                >
                  <option value="">All statuses</option>
                  <option value="completed_journey">Completed Journey</option>
                  <option value="left_route_or_no_end_capture">Start Seen / No End Capture</option>
                  <option value="entered_route_or_no_prior_start">End Seen / No Prior Start</option>
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Min Journey Minutes</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={reportParameters.min_journey_minutes}
                  onChange={(e) => updateReportParameter("min_journey_minutes", e.target.value)}
                  className={parameterInputClass}
                />
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Max Journey Minutes</label>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={reportParameters.max_journey_minutes}
                  onChange={(e) => updateReportParameter("max_journey_minutes", e.target.value)}
                  className={parameterInputClass}
                />
              </div>
            </>
          ) : configUsesDurationStaySingleCamera ? (
            <>
              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Camera</label>
                <select
                  value={reportParameters.camera_device_id}
                  onChange={(e) => {
                    const selected = parameterCameraOptions.find((camera) => camera.id === e.target.value);
                    updateReportParameter("camera_device_id", e.target.value);
                    updateReportParameter("camera_name", selected?.name || "");
                  }}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id || loadingParameterCameras}
                >
                  <option value="">
                    {!reportParameters.job_id
                      ? "Select job first"
                      : loadingParameterCameras
                      ? "Loading cameras..."
                      : "Select camera"}
                  </option>
                  {parameterCameraOptions.map((camera) => (
                    <option key={camera.id} value={camera.id}>
                      {camera.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Entry Direction for Selected Camera</label>
                <select
                  value={reportParameters.entry_motion}
                  onChange={(e) => updateReportParameter("entry_motion", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id}
                >
                  <option value="towards">Towards = Entry</option>
                  <option value="away">Away = Entry</option>
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Min Dwell Minutes</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={reportParameters.min_dwell_minutes}
                  onChange={(e) => updateReportParameter("min_dwell_minutes", e.target.value)}
                  className={parameterInputClass}
                />
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Max Dwell Minutes</label>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={reportParameters.max_dwell_minutes}
                  onChange={(e) => updateReportParameter("max_dwell_minutes", e.target.value)}
                  className={parameterInputClass}
                />
              </div>
            </>
          ) : (
            <>
              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Route</label>
                <select
                  value={reportParameters.route_code}
                  onChange={(e) => updateReportParameter("route_code", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id || loadingParameterRoutes}
                >
                  <option value="">
                    {!reportParameters.job_id
                      ? "Select job first"
                      : loadingParameterRoutes
                      ? "Loading routes..."
                      : "All routes"}
                  </option>
                  {parameterRouteOptions.map((route) => (
                    <option key={route.route_code} value={route.route_code}>
                      {route.route_label || route.route_code}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Scenario</label>
                <select
                  value={reportParameters.scenario_code}
                  onChange={(e) => updateReportParameter("scenario_code", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id}
                >
                  <option value="">All scenarios</option>
                  {SCENARIO_OPTIONS.map((scenario) => (
                    <option key={scenario.code} value={scenario.code}>
                      {scenario.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs text-gray-600 mb-1">Pairing</label>
                <select
                  value={reportParameters.pairing_id}
                  onChange={(e) => updateReportParameter("pairing_id", e.target.value)}
                  className={parameterInputClass}
                  disabled={!reportParameters.job_id || loadingParameterRoutes}
                >
                  <option value="">
                    {!reportParameters.job_id
                      ? "Select job first"
                      : loadingParameterRoutes
                      ? "Loading pairings..."
                      : "All pairings"}
                  </option>
                  {parameterPairingOptions
                    .filter((pairing) => {
                      if (!reportParameters.route_code) return true;
                      return pairing.route_code === reportParameters.route_code;
                    })
                    .map((pairing) => (
                      <option key={pairing.id} value={pairing.id}>
                        {pairing.route_label || pairing.route_code || `Pairing ${pairing.id}`} ·{" "}
                        {pairing.start_camera_name || pairing.start_camera_device_id} →{" "}
                        {pairing.end_camera_name || pairing.end_camera_device_id}
                      </option>
                    ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="text-[11px] text-gray-500 mt-2">
          {configUsesSightingsPairings
            ? "Pairings uses the selected ANPR job, named time slots, and the two movement timing thresholds. Observed movements are not restricted to configured camera-pairing routes."
            : configUsesJourneyTimeCorridor
            ? "Journey Time uses the selected camera pairing, pairing distance, date range, time window, scenario/status, and journey-time limits."
            : configUsesDurationStaySingleCamera
            ? "Duration of Stay uses the selected ANPR job, camera, date range, time window, dwell limits, and camera direction/orientation."
            : "These filters are sent to the report engine and match the Weaving / Average Speed widget context."}
        </div>
      </div>
    ) : null;

  const configModal =
    showConfigModal && configReport
      ? ReactDOM.createPortal(
          <ModalWrapper onClose={closeConfig} size="xl">
            <div
              className="vx-standard-reports-config-shell w-full"
              style={{
                width: "100%",
                maxWidth: "980px",
                minWidth: 0,
                maxHeight: "86vh",
                padding: 0,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div className="shrink-0 border-b border-gray-200 px-3 pb-3 pt-3 sm:px-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold break-words">
                    {configReport.display_name || getDisplayReportName(configReport)}
                  </h2>
                  <div
                    className="text-sm text-gray-500 mt-1"
                    style={{ maxWidth: 560, lineHeight: 1.35 }}
                  >
                    Choose fields, set order, then preview.
                  </div>
                  <div
                    className="text-[11px] text-gray-500 mt-2"
                    style={{ lineHeight: 1.35 }}
                  >
                    Source:{" "}
                    <span className="font-mono break-all">
                      {configReportSource || "template/model-backed"}
                    </span>
                  </div>
                </div>
              </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 pb-3 pt-3 sm:px-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 shrink-0">
                <div className="min-w-0">
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="dd/MM/yyyy"
                    wrapperClassName="w-full"
                    className="min-h-[44px] w-full rounded border px-2 py-2 text-sm md:min-h-0"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="dd/MM/yyyy"
                    wrapperClassName="w-full"
                    className="min-h-[44px] w-full rounded border px-2 py-2 text-sm md:min-h-0"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={timeStart}
                    onChange={(e) => setTimeStart(e.target.value)}
                    className="min-h-[44px] w-full rounded border px-2 py-2 text-sm md:min-h-0"
                    disabled={configUsesTimeSlots}
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-xs text-gray-600 mb-1">End Time</label>
                  <input
                    type="time"
                    value={timeEnd}
                    onChange={(e) => setTimeEnd(e.target.value)}
                    className="min-h-[44px] w-full rounded border px-2 py-2 text-sm md:min-h-0"
                    disabled={configUsesTimeSlots}
                  />
                </div>
              </div>

              {configUsesTimeSlots && (
                <div className="text-[11px] text-gray-500 -mt-2 mb-3 shrink-0">
                  Single Start/End Time is disabled because this report uses named time slots.
                </div>
              )}

              {timeSlotControls}
              {parameterControls}

              <div className="mb-3 shrink-0 flex items-start gap-2 min-w-0">
                <input
                  id="auto-uncheck-empty-columns"
                  type="checkbox"
                  checked={autoUncheckEmptyColumns}
                  onChange={(e) => setAutoUncheckEmptyColumns(e.target.checked)}
                  className="mt-[2px] shrink-0"
                />
                <label
                  htmlFor="auto-uncheck-empty-columns"
                  className="text-sm text-gray-700 leading-5"
                >
                  Uncheck empty columns automatically after preview query
                </label>
              </div>

              <div className="mb-3 grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <button onClick={selectAllFields} className={controlBtnClass}>
                  Select All
                </button>

                <button onClick={clearAllFields} className={controlBtnClass}>
                  Clear All
                </button>

                <button onClick={resetDefaultFields} className={controlBtnClass}>
                  Reset Default
                </button>

                <button onClick={handleSaveLayout} className={controlBtnClass}>
                  Save Layout
                </button>

                {canUpdateTemplate && (
                  <button onClick={handleUpdateTemplate} className={controlBtnClass}>
                    Update Template
                  </button>
                )}

                {configLoadingFields && (
                  <span className="text-xs text-gray-500 self-center">
                    Loading live fields...
                  </span>
                )}
              </div>

              <div className="text-xs text-gray-500 mb-2 shrink-0">
                Selected: {selectedFields.length} / {configAllFields.length}
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div className="border rounded p-2 min-w-0 flex flex-col">
                  <div className="text-sm font-semibold text-gray-800 mb-1 shrink-0">
                    Available Fields
                  </div>
                  <div className="text-xs text-gray-500 mb-2 shrink-0">
                    Tick to include. New fields go to the end.
                  </div>

                  <div className="overflow-x-hidden pr-1 lg:max-h-[320px] lg:overflow-y-auto">
                    <div className="grid grid-cols-1 gap-2">
                      {configAllFields.map((field) => {
                        const checked = selectedFieldsSet.has(field);
                        return (
                          <label
                            key={field}
                            className="flex items-start gap-2 text-sm min-w-0"
                            title={`${FRIENDLY_FIELD_NAMES[field] || field} (${field})`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleField(field)}
                              className="mt-[2px] shrink-0"
                            />
                            <span className="break-words leading-5 min-w-0">
                              <span className="font-medium">
                                {FRIENDLY_FIELD_NAMES[field] || field}
                              </span>
                              {FRIENDLY_FIELD_NAMES[field] && (
                                <span className="text-gray-500"> — {field}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="border rounded p-2 min-w-0 flex flex-col">
                  <div className="text-sm font-semibold text-gray-800 mb-1 shrink-0">
                    Selected Fields Order
                  </div>
                  <div className="text-xs text-gray-500 mb-2 shrink-0">
                    This order is used in preview and export.
                  </div>

                  <div className="overflow-x-hidden pr-1 lg:max-h-[320px] lg:overflow-y-auto">
                    {!selectedFields.length ? (
                      <div className="text-sm text-gray-500">No fields selected.</div>
                    ) : (
                      <div className="space-y-2">
                        {selectedFields.map((field, index) => {
                          const isFirst = index === 0;
                          const isLast = index === selectedFields.length - 1;

                          return (
                            <div
                              key={`selected-${field}`}
                              className="border rounded p-2 flex items-start gap-2 min-w-0"
                            >
                              <div
                                className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                                style={{
                                  background: accent,
                                  color: "#fff",
                                }}
                                title={`Position ${index + 1}`}
                              >
                                {index + 1}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium break-words">
                                  {FRIENDLY_FIELD_NAMES[field] || field}
                                </div>
                                <div className="text-xs text-gray-500 break-words">{field}</div>

                                <div className="flex flex-wrap gap-1 mt-2">
                                  <button
                                    type="button"
                                    onClick={() => moveSelectedFieldToTop(field)}
                                    disabled={isFirst}
                                    style={isFirst ? disabledOrderPillStyle : orderPillStyle}
                                    className="vx-report-order-button"
                                    title="Move to top"
                                  >
                                    Top
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => moveSelectedFieldUp(field)}
                                    disabled={isFirst}
                                    style={isFirst ? disabledOrderPillStyle : orderPillStyle}
                                    className="vx-report-order-button"
                                    title="Move up"
                                  >
                                    ↑
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => moveSelectedFieldDown(field)}
                                    disabled={isLast}
                                    style={isLast ? disabledOrderPillStyle : orderPillStyle}
                                    className="vx-report-order-button"
                                    title="Move down"
                                  >
                                    ↓
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => moveSelectedFieldToBottom(field)}
                                    disabled={isLast}
                                    style={isLast ? disabledOrderPillStyle : orderPillStyle}
                                    className="vx-report-order-button"
                                    title="Move to bottom"
                                  >
                                    Bottom
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {!!availableUnselectedFields.length && (
                    <div className="pt-2 mt-2 border-t shrink-0 min-w-0">
                      <div className="text-xs text-gray-500 break-words">
                        Unselected fields:{" "}
                        {availableUnselectedFields
                          .map((field) => FRIENDLY_FIELD_NAMES[field] || field)
                          .join(", ")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              </div>

              <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-gray-200 bg-white px-3 py-3 sm:flex sm:justify-end sm:gap-3 sm:px-4">
                <button onClick={closeConfig} className={controlBtnClass}>
                  Cancel
                </button>

                <button onClick={handlePreviewConfigured} className={previewBtnClass}>
                  Preview
                </button>
              </div>
            </div>
          </ModalWrapper>,
          document.body
        )
      : null;

  if (loading) {
    return <div className="text-sm text-gray-500">Loading reports...</div>;
  }

  if (!reports.length) {
    return <div className="text-sm text-gray-500">No standard reports found.</div>;
  }

  return (
    <>
      <div
        ref={widgetRootRef}
        className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      >
        <style>{`
          @media (max-width: 767px) {
            .vx-standard-reports-content .react-datepicker-wrapper {
              width: 100%;
            }

            .vx-standard-report-card {
              scroll-snap-align: start;
              scroll-snap-stop: always;
            }

            .vx-report-order-button {
              min-height: 44px;
              min-width: 44px;
            }

            .vx-standard-reports-config-shell {
              height: 82dvh;
              max-height: 82dvh !important;
            }

            .vx-standard-reports-config-shell .react-datepicker-wrapper {
              width: 100%;
            }
          }
        `}</style>

        <div ref={contentRef} className="vx-standard-reports-content space-y-3 p-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4">
          <div className="w-full sm:w-auto">
            <label className="block text-xs text-gray-600 mb-1">Start Date</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="dd/MM/yyyy"
              wrapperClassName="w-full"
              className="min-h-[44px] w-full rounded border px-2 py-2 text-sm sm:w-[140px] sm:min-h-0 sm:py-1"
            />
          </div>

          <div className="w-full sm:w-auto">
            <label className="block text-xs text-gray-600 mb-1">End Date</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="dd/MM/yyyy"
              wrapperClassName="w-full"
              className="min-h-[44px] w-full rounded border px-2 py-2 text-sm sm:w-[140px] sm:min-h-0 sm:py-1"
            />
          </div>

          <div className="hidden flex-1 sm:block" />

          <button
            onClick={fetchReports}
            className={controlBtnClass}
            title="Refresh templates"
          >
            Refresh
          </button>
        </div>

        <div className="space-y-2">
          {grouped.map(([groupName, items]) => {
            const isOpen = !!openGroups[groupName];
            const count = items?.length || 0;

            return (
              <div
                key={groupName}
                className="bg-white border border-gray-200 rounded shadow-sm overflow-hidden"
              >
                <div
                  className="px-3 py-2 flex items-center justify-between"
                  style={{ background: primary }}
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    className="flex min-h-[44px] w-full min-w-0 items-center gap-2 text-left text-sm font-semibold"
                    style={{ color: groupBtnColor }}
                  >
                    <span
                      className="inline-flex items-center justify-center w-6 h-6 rounded"
                      style={{
                        border: `1px solid ${accent}`,
                        color: "#fff",
                        fontSize: 12,
                        lineHeight: "12px",
                        background: "rgba(255,255,255,0.12)",
                      }}
                      aria-hidden="true"
                    >
                      {isOpen ? "▾" : "▸"}
                    </span>

                    <span className="min-w-0 flex-1 break-words">{groupName}</span>

                    <span
                      className="text-[11px] px-2 py-[2px] rounded-full"
                      style={{
                        border: `1px solid rgba(255,255,255,0.7)`,
                        color: "#fff",
                        background: "rgba(255,255,255,0.12)",
                        fontWeight: 800,
                      }}
                      title={`${count} reports`}
                    >
                      {count}
                    </span>
                  </button>
                </div>

                {isOpen && (
                  <div className="p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {items.map((r) => {
                        const { top, extraCount } = summarizeFields(r?.fields);
                        const hasParameters = isAnprParameterizedReport(r);
                        const hasTimeSlots = isTimeSlotReport(r);

                        return (
                          <div
                            key={r.id}
                            className="vx-standard-report-card min-w-0 bg-white border border-gray-200 rounded p-3 shadow-sm flex flex-col justify-between"
                          >
                            <div className="mb-2">
                              <div className="break-words text-sm font-semibold text-gray-800 leading-snug">
                                {r.display_name || getDisplayReportName(r)}
                              </div>

                              {hasParameters && (
                                <div
                                  className="text-[11px] font-semibold mt-1"
                                  style={{ color: accent }}
                                >
                                  {isAnprSightingsPairingsReport(r)
                                    ? "Requires ANPR job / pairing timing parameters"
                                    : isAnprDurationStaySingleCameraReport(r)
                                    ? "Requires ANPR job / camera / dwell parameters"
                                    : "Requires ANPR job / route parameters"}
                                </div>
                              )}

                              {hasTimeSlots && (
                                <div
                                  className="text-[11px] font-semibold mt-1"
                                  style={{ color: accent }}
                                >
                                  Supports server-side time-slot filtering
                                </div>
                              )}

                              <div className="mt-1 break-words text-[11px] text-gray-500">
                                {top.length ? (
                                  <>
                                    Fields:{" "}
                                    {top.map((f) => FRIENDLY_FIELD_NAMES[f] || f).join(", ")}
                                    {extraCount > 0 ? ` +${extraCount} more` : ""}
                                  </>
                                ) : (
                                  "Fields: (none)"
                                )}
                              </div>
                            </div>

                            <button onClick={() => openConfig(r)} className={previewBtnClass}>
                              Configure & Preview
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

          {showPreviewModal && selectedReport && previewData && (
            <ReportDisplayModal
              onClose={() => setShowPreviewModal(false)}
              data={previewData.data}
              fields={selectedReport.fields}
              uid={previewData.timestamp_uid}
              meta={{
                name: selectedReport.display_name || getDisplayReportName(selectedReport),
                start_date: toNiceDate(startDate),
                end_date: toNiceDate(endDate),
                row_count: previewData.row_count,
                parameters: previewData.parameters || {},
                time_slots: previewData.time_slots || [],
                time_start: previewData.time_start || null,
                time_end: previewData.time_end || null,
                preview_limit: previewData.preview_limit || null,
                export_payload: previewData.export_payload || null,
              }}
            />
          )}
        </div>
      </div>

      {configModal}
    </>
  );
};

export default StandardReportsWidget;
